import React, { useState, useEffect, useCallback } from 'react';
import {
  TableCellsIcon,
  ArrowLeftIcon,
  PlusIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  CpuChipIcon,
  DocumentMagnifyingGlassIcon,
  ChartBarIcon,
  ChatBubbleBottomCenterTextIcon
} from '@heroicons/react/24/outline';
import taskFlowApi from '../../../api/taskFlow';
import agentOneAPI from 'api/agentOne';
import ChatSidebar from './ChatSidebar';
import { useSelector } from 'react-redux';
import ChatHistoryList from './ChatHistoryList';
import uuidv4 from 'utils/uuidv4';
import { dispatch } from 'store';
import Button from '../components/Button'
import ChatInput from './ChatInput';
import Lottie from 'lottie-react';
import assistantAnimation from '../../../assets/assistant.json';

const TableAssistant = ({ onBack }) => {
  const currentSheetData = useSelector(state => state.sheets.sheets.find(s => s._id === state.sheets.activeSheetId));
  const tableId = currentSheetData?._id;
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [activeTab, setActiveTab] = useState('chat');
  const [sessionId, setSessionId] = useState(null);
  const [allChatHistory, setAllChatHistory] = useState([]);
  const [selectedHistoryChat, setSelectedHistoryChat] = useState(null);
  const [flow, setFlow] = useState(null);

  const onClick = (e, item) => {
    dispatch({ type: 'NAV_ITEM_CLICKED', payload: null })
  }

  const tableChatSuggestions = [
    {
      id: 1,
      title: "Add New Column",
      description: "Add new columns with specific data types and configurations",
      icon: TableCellsIcon,
      prompt: "Help me add a new column to this table with proper data type and configuration",
      color: "bg-blue-50 text-blue-600 border-blue-200"
    },
    {
      id: 2,
      title: "Column Tools Setup",
      description: "Configure tools and AI models for column processing",
      icon: WrenchScrewdriverIcon,
      prompt: "Help me set up the right tools and AI models for processing data in this column",
      color: "bg-green-50 text-green-600 border-green-200"
    },
    {
      id: 3,
      title: "Model Suggestions",
      description: "Get recommendations for the best AI models for your task",
      icon: CpuChipIcon,
      prompt: "Suggest the best AI model for my column task and data processing requirements",
      color: "bg-purple-50 text-purple-600 border-purple-200",
      disabled: true
    },
    {
      id: 4,
      title: "Debug Cell Runs",
      description: "Find and fix issues with cell execution and data processing",
      icon: ExclamationTriangleIcon,
      prompt: "Help me debug the cell run errors and explain what went wrong with the data processing",
      color: "bg-red-50 text-red-600 border-red-200",
      disabled: true
    },
    {
      id: 5,
      title: "Data Analysis",
      description: "Analyze patterns and insights in your table data",
      icon: ChartBarIcon,
      prompt: "Analyze the data patterns in this table and provide insights about the results",
      color: "bg-orange-50 text-orange-600 border-orange-200",
      disabled: true
    },
    {
      id: 6,
      title: "Table Optimization",
      description: "Optimize table performance and data processing efficiency",
      icon: DocumentMagnifyingGlassIcon,
      prompt: "Help me optimize this table's performance and improve data processing efficiency",
      color: "bg-indigo-50 text-indigo-600 border-indigo-200",
      disabled: true
    }
  ];


  const loadChatHistory = useCallback(async () => {
    if (!currentSheetData?._id) return;

    try {
      // Load existing chat history for this table
      const response = await taskFlowApi.getChatHistoryTable(currentSheetData?._id);
      setAllChatHistory(response.data || []);
    } catch (error) {
      console.error('Failed to load chat history:', error);
      setAllChatHistory([]);
    }
  }, [currentSheetData]);

  const handleTabChange = (tab) => {
    if(tab==='chat'){
      setChatHistory([]);
    }
    setActiveTab(tab);
  };

  useEffect(() => {
      loadChatHistory();
  }, [tableId, activeTab, loadChatHistory]);


  // Simplified main message handler using agentOne API
  const handleSendMessage = async (m) => {
    setLoading(true);
    try {
      let sId = sessionId
      if (sId === null) {
        sId = uuidv4();
        setSessionId(sId)
      }
      const userMessage = { role: 'user', content: m ?? message };
      setChatHistory(prev => [...prev, userMessage]);

      const payload = {
        message: m ?? message,
        sessionId: sId,
        tableId,
        type: 'table_assistant'
      };
      setIsTyping(true);
      setStreamingMessage('');
      setMessage('');

      // Add token callback to handle streaming tokens
      const streamPayload = {
        ...payload,
        onToken: (token, fullContent) => {
          setStreamingMessage(fullContent);
        }
      };

      // Use agentOne streaming chat
      const result = await agentOneAPI.chatStream2(streamPayload);
      setIsTyping(false);
      setStreamingMessage('');
      console.log('AgentOne response:', result);

      // Update state based on response
      if (result.success) {
        setSuggestedActions(result.suggestions || []);
        setFlow(result);
        setChatHistory(result.chatHistory);

      } else {
        setChatHistory(prev => [...prev, {
          role: 'assistant',
          content: result.message || 'Sorry, I encountered an error processing your request.'
        }]);
      }

      setMessage('');
      setLoading(false);

    } catch (error) {
      console.error('Error sending message:', error);
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again.'
      }]);
      setLoading(false);
    }
  };

  // Simplified sendMessage - always uses enhanced assistant
  const sendMessage = async () => {
    await handleSendMessage();
  };

  // Quick action handler for workflow buttons
  const handleQuickAction = async (messageText) => {
    try {
      console.log(`Executing quick action: ${messageText}`);
      setMessage(messageText);
      handleSendMessage(messageText);
    } catch (error) {
      console.error(`Error executing ${messageText}:`, error);
    }
  };

  const handleSuggestionClick = (prompt) => {
    setMessage(prompt);
    setChatHistory([]); // Start fresh chat
    handleSendMessage(prompt);

  };
  const loadFlowHistory = useCallback(async (flowId) => {
    try {
      const response = await taskFlowApi.getTaskFlow(flowId);
      if (response.data) {
        setChatHistory(response.data.chatHistory || []);
      }
    } catch (error) {
      console.error('Failed to load flow history:', error);
      setChatHistory([]);
    }
  }, []);

  const handleHistoryClick = (historyItem) => {
    setSelectedHistoryChat(historyItem);
    setActiveTab('chat');
    setChatHistory(historyItem.chatHistory || []);
    if (historyItem && historyItem.flowId) {
      loadFlowHistory(historyItem.flowId);
      setSessionId(historyItem.flowId);
    }
  };
  const renderHistoryView = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (allChatHistory.length === 0) {
      return (
        <div className="text-center py-8">
          <ClockIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No chat history</h3>
          <p className="text-sm text-gray-500 mb-4">
            Start a conversation to see your chat history here
          </p>
          <button
            onClick={() => handleTabChange('new-chat')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Start New Chat
          </button>
        </div>
      );
    }

    return (
      <ChatHistoryList
        chatHistoryData={allChatHistory}
        onChatClick={handleHistoryClick}
      />
    );
  };

  const renderNewChatView = () => {
    return (
      <div className="w-full">
        {/* Welcome Section */}
        <div className="text-left mb-6">
         
          <p className="text-sm text-gray-600">
            Get help with columns, tools, debugging, and data analysis
          </p>
        </div>



        {/* Suggestions */}
        <div className="space-y-3">

          {tableChatSuggestions.filter(a => !a.disabled).map((suggestion) => {
            const IconComponent = suggestion.icon;
            const isDisabled = suggestion.disabled;
            return (
              <button
                key={suggestion.id}
                onClick={() => !isDisabled && handleSuggestionClick(suggestion.prompt)}
                disabled={isDisabled}
                className={`w-full p-3 border rounded-lg transition-all duration-200 text-left group ${isDisabled
                  ? 'cursor-not-allowed'
                  : `hover:shadow-md ${suggestion.color.replace('bg-', 'hover:bg-').replace('-50', '-100')}`
                  }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${suggestion.color}`}>
                    <IconComponent className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h5 className="text-sm font-medium text-gray-900 group-hover:text-gray-800">
                        {suggestion.title}
                      </h5>
                      {isDisabled && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 line-clamp-2">
                      {suggestion.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen w-full bg-white flex flex-col">
      {/* Header with tabs */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3 align-center">
          <div className="flex items-center gap-2">
            <div className="w-[50px] h-[50px]">
              <Lottie 
                animationData={assistantAnimation} 
                loop={false}
                autoplay={false}
              />
            </div>
            <h2 className="text-md font-normal text-gray-900">Table Assistant</h2>
          </div>
          <div className="flex items-center justify-between  gap-x-3">
            {
              activeTab === 'history' ? (
                <button
                  onClick={() => handleTabChange('chat')}
                  className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"

                >
                  Chat
                </button>
              )
                : allChatHistory.length > 0 ? (
                  <button
                    onClick={() => handleTabChange('history')}
                    className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"

                  >
                    History
                  </button>
                ) : null
            }
             {activeTab === 'chat' && chatHistory.length > 0 ? (
                  <button
                  onClick={() => handleTabChange('chat')}
                  className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"

                >
                  New Chat
                </button>
                ) : null
              }


            <button
              onClick={(e) => onClick(e, null)}
              className="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
              title="Close table assistant"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          </div>
        </div>


      </div>

      {/* Content */}
      <div className="flex-1 flex min-h-0">
        {activeTab === 'history' && (
          <div className="w-full h-full flex flex-col">
            <div className="flex-1 min-h-0 p-4 overflow-y-auto">
              {renderHistoryView()}
            </div>
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="w-full h-full flex flex-col">
            {
              chatHistory.length > 0 ? (
                <ChatSidebar
                  isSidebarOpen={true}
                  setIsSidebarOpen={() => { }}
                  title={''}
                  chatHistory={chatHistory}
                  message={message}
                  setMessage={setMessage}
                  sendMessage={sendMessage}
                  loading={loading}
                  isTyping={isTyping}
                  streamingMessage={streamingMessage}
                  suggestedActions={suggestedActions}
                  handleQuickAction={handleQuickAction}
                  showActionButtons={true}
                  customWidth="w-full"
                  hideBubble
                  isTable
                />
              )
                :
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="flex-1 p-4 overflow-y-auto">
                    {renderNewChatView()}
                  </div>
                  <div className="flex-shrink-0 p-4 pt-0">
                    <p className="text-sm text-gray-500 text-left py-4">Or type a question to get started</p>
                    <ChatInput value={message} onChange={setMessage} sendMessage={sendMessage} loading={loading} isTable />
                  </div>
                </div>
            }
          </div>
        )}


      </div>
    </div>
  );
};

export default TableAssistant;